import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FilePen, FileSignature, Lock, Map as MapIcon, PanelRightClose, PanelRightOpen, RefreshCw, RotateCcw, Trash2, Unlock } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { ApiError, api } from "../../api/client";
import type { Frago, FragoFieldSpec } from "../../api/types";
import { attentionSelector, designate } from "../../components/attention";
import { Banner } from "../../components/Callout";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { EmptyState } from "../../components/EmptyState";
import { Motion, Presence } from "../../components/presence";
import { SidePanel } from "../../components/SidePanel";
import { Badge, Button, IconButton, textareaClass } from "../../components/ui";
import { Waiting } from "../../components/Waiting";
import { t } from "../../i18n/i18n";
import { plural } from "../../plural";
import { designateDossierReadOnly, useWriteBlock } from "../../sharing/access";
import { useUi } from "../../state/ui";
import { useCoherence } from "../coherence/coherenceState";
import { ExportMenu } from "./ExportMenu";
import { OrderPage } from "./OrderPage";
import { SignaturePad } from "./SignaturePad";

/** Cible désignée quand le FRAGO attend sa validation, ou quand un champ refusé est à corriger. */
export const FRAGO_APPROVE = "frago-approve";
const fieldTarget = (key: string) => `frago-field-${key}`;

/** Intitulés des champs du FRAGO dans l'éditeur (clés françaises du serveur, traduites ici). */
function fieldLabel(spec: FragoFieldSpec): string {
  return t(spec.label);
}

function errorOf(err: unknown): { text: string; field: string | null } {
  if (err instanceof ApiError) {
    const field = typeof err.detail?.field === "string" ? err.detail.field : null;
    return { text: err.message, field };
  }
  return { text: t("Opération impossible"), field: null };
}

/** Un FRAGO de la rotation CENTAC dans la vue de l'ordre (docs/08 §27) : pages identiques au PDF, éditeur des
 *  paragraphes (vide = « Sans changement »), mise à jour d'après la carte de son OPO, signature, validation,
 *  exports. L'action attendue pulse : valider un FRAGO en rédaction. */
export function FragoView({ exerciseId, number, onBack }: { exerciseId: string; number: number; onBack: () => void }) {
  const queryClient = useQueryClient();
  const notify = useUi((s) => s.notify);
  const setView = useUi((s) => s.setView);
  const writeBlock = useWriteBlock();
  const locked = writeBlock.blocked;
  const [activeField, setActiveField] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(() => window.matchMedia("(min-width: 1280px)").matches);
  const [dialog, setDialog] = useState<null | "signature" | "delete">(null);
  const fragoKey = ["frago", exerciseId, number];
  const fragoQuery = useQuery({
    queryKey: fragoKey,
    queryFn: () => api.frago(exerciseId, number),
    // Le FRAGO en rédaction suit la carte de son OPO : relu toutes les 20 s tant que la vue est visible.
    refetchInterval: () => (document.visibilityState === "visible" ? 20_000 : false),
    refetchOnWindowFocus: true,
  });
  const frago = fragoQuery.data;
  const layoutQuery = useQuery({
    queryKey: ["frago-layout", exerciseId, number, frago?.version, frago?.has_signature, frago?.language],
    queryFn: () => api.fragoLayout(exerciseId, number),
    enabled: Boolean(frago),
    placeholderData: (prev, prevQuery) => (prevQuery?.queryKey[2] === number ? prev : undefined),
  });
  const specsQuery = useQuery({ queryKey: ["frago-fields", exerciseId, number], queryFn: () => api.fragoFields(exerciseId, number) });

  const applied = (data: Frago) => {
    queryClient.setQueryData(fragoKey, data);
    void queryClient.invalidateQueries({ queryKey: ["fragos", exerciseId] });
  };
  const failed = (err: unknown) => {
    const { text, field } = errorOf(err);
    notify({ text, tone: "error" });
    if (field) {
      setActiveField(field);
      setEditorOpen(true);
      window.setTimeout(() => designate(attentionSelector(fieldTarget(field))), 120);
    }
  };
  const edit = useMutation({
    mutationFn: (input: { key: string; value: string }) =>
      api.editFrago(exerciseId, number, frago?.version ?? 0, { [input.key]: input.value }),
    onSuccess: applied,
    onError: failed,
  });
  const reset = useMutation({ mutationFn: (key: string) => api.resetFragoField(exerciseId, number, key), onSuccess: applied, onError: failed });
  const recompose = useMutation({
    mutationFn: () => api.recomposeFrago(exerciseId, number),
    onSuccess: (data) => {
      applied(data);
      notify({ text: t("FRAGO N°{n} mis à jour d'après la carte", { n: number }), tone: "ok" });
    },
    onError: failed,
  });
  // Cohérence tactique (A118) : un point bloquant du FRAGO (paragraphe changé par la carte imprimé « Sans
  // changement », mission de posture changée) ouvre le panneau ; valider ou passer outre y rend la main.
  const openCoherenceGate = useCoherence((s) => s.open);
  const confirmed = useCoherence((s) => s.confirmed);
  const finalize = useMutation({
    mutationFn: (override?: string) => api.finalizeFrago(exerciseId, number, override),
    onSuccess: (data) => {
      applied(data);
      void queryClient.invalidateQueries({ queryKey: ["coherence", exerciseId] });
      notify({ text: t("FRAGO N°{n} validé", { n: number }), detail: t("Diffusion consignée au journal de la DIREX."), tone: "ok" });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === "COHERENCE_BLOCKING") {
        openCoherenceGate({ kind: "approve", number, oplan: false, frago: true });
        setView("coherence");
        return;
      }
      failed(err);
    },
  });
  const finalizeMutate = finalize.mutate;
  useEffect(() => {
    if (!confirmed) return;
    const taken = useCoherence.getState().takeConfirmed(number);
    if (taken) finalizeMutate(taken.override);
  }, [confirmed, number, finalizeMutate]);
  const reopen = useMutation({ mutationFn: () => api.reopenFrago(exerciseId, number), onSuccess: applied, onError: failed });
  const sign = useMutation({
    mutationFn: (png: string) => api.signFrago(exerciseId, number, png),
    onSuccess: (data) => {
      applied(data);
      setDialog(null);
    },
    onError: failed,
  });
  const unsign = useMutation({
    mutationFn: () => api.unsignFrago(exerciseId, number),
    onSuccess: (data) => {
      applied(data);
      setDialog(null);
    },
    onError: failed,
  });
  const remove = useMutation({
    mutationFn: () => api.deleteFrago(exerciseId, number),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["fragos", exerciseId] });
      queryClient.removeQueries({ queryKey: fragoKey });
      setDialog(null);
      notify({ text: t("FRAGO N°{n} supprimé", { n: number }), tone: "ok" });
      onBack();
    },
    onError: failed,
  });

  const specs = specsQuery.data?.fields ?? [];
  const editable = useMemo(() => new Set(specs.map((s) => s.key)), [specs]);
  if (!frago) {
    return fragoQuery.isError ? (
      <EmptyState variant="error" compact>
        {errorOf(fragoQuery.error).text}
      </EmptyState>
    ) : (
      <EmptyState variant="loading" compact title={t("Préparation du FRAGO…")} />
    );
  }
  const draft = frago.status === "draft";
  const busy = finalize.isPending || recompose.isPending || reopen.isPending;
  const base = `/api/exercises/${exerciseId}/fragos/${number}`;
  const imageUrl = (name: string) => (name === "signature" ? `${base}/signature.png?v=${frago.version}` : null);
  const lockedProps = locked ? { blocked: true, onBlocked: () => designateDossierReadOnly() } : {};
  const opo = frago.opo;
  const changedCount = frago.changed.length;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-line bg-surface px-4 py-2">
        <span className="text-ui font-semibold text-ink">{t("FRAGO N°{n}", { n: number })}</span>
        {draft ? <Badge tone="warn">{t("FRAGO en rédaction")}</Badge> : <Badge tone="ok">{t("Validé")}</Badge>}
        <Badge>
          {changedCount
            ? plural(changedCount, t("paragraphe modifié"), t("paragraphes modifiés"))
            : t("Aucun paragraphe modifié depuis l'OPO")}
        </Badge>
        {frago.incident && <Badge tone="accent">{t("Incident {code}", { code: frago.incident.code })}</Badge>}
        <div className="ml-auto flex flex-wrap items-center gap-1.5">
          <Button size="sm" variant="secondary" onClick={() => setView("manoeuvre")} title={t("Ouvrir la carte de l'OPO N°{n}", { n: opo })}>
            <MapIcon size={14} /> {t("Carte de l'OPO N°{n}", { n: opo })}
          </Button>
          {draft && (
            <Button size="sm" variant="secondary" onClick={() => recompose.mutate()} disabled={busy} {...lockedProps}>
              <RefreshCw size={14} /> {recompose.isPending ? <Waiting>{t("Mise à jour…")}</Waiting> : t("Mettre à jour d'après la carte")}
            </Button>
          )}
          {draft && (
            <Button size="sm" variant="secondary" onClick={() => setDialog("signature")} {...lockedProps}>
              <FileSignature size={14} /> {frago.has_signature ? t("Signature") : t("Signer")}
            </Button>
          )}
          {draft && frago.last && (
            <IconButton size="sm" variant="danger" title={t("Supprimer le FRAGO")} aria-label={t("Supprimer le FRAGO")} onClick={() => setDialog("delete")} {...lockedProps}>
              <Trash2 size={15} />
            </IconButton>
          )}
          {draft ? (
            <Button
              size="sm"
              data-attention={FRAGO_APPROVE}
              highlight={!locked && !busy}
              onClick={() => finalize.mutate(undefined)}
              disabled={busy}
              {...lockedProps}
            >
              <Lock size={14} /> {finalize.isPending ? <Waiting>{t("Validation…")}</Waiting> : t("Valider le FRAGO")}
            </Button>
          ) : (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => reopen.mutate()}
              disabled={busy}
              blocked={locked || !frago.last}
              onBlocked={() => {
                if (locked) return designateDossierReadOnly();
                notify({ text: t("Seul le dernier FRAGO de l'OPO N°{n} peut être rouvert.", { n: opo }), tone: "info" });
              }}
            >
              <Unlock size={14} /> {t("Rouvrir")}
            </Button>
          )}
          <ExportMenu formats={["pdf", "docx", "odt", "txt"]} href={(fmt) => `${base}/export/${fmt}`} title={t("Exporter le FRAGO")} variant={draft ? "secondary" : "primary"} />
          <IconButton
            size="sm"
            active={editorOpen}
            title={editorOpen ? t("Masquer l'éditeur") : t("Afficher l'éditeur")}
            aria-label={editorOpen ? t("Masquer l'éditeur") : t("Afficher l'éditeur")}
            onClick={() => setEditorOpen((o) => !o)}
          >
            {editorOpen ? <PanelRightClose size={16} /> : <PanelRightOpen size={16} />}
          </IconButton>
        </div>
      </div>
      <Presence show={draft}>
        {draft && (
          <Banner tone="info">
            {frago.previous
              ? t("Paragraphes relevés depuis l'état en vigueur après le FRAGO N°{p} ; le FRAGO suit la carte de l'OPO N°{n} jusqu'à sa validation.", { p: frago.previous, n: opo })
              : t("Paragraphes relevés depuis l'OPO N°{n} validé ; le FRAGO suit la carte de cet OPO jusqu'à sa validation. Un paragraphe vide s'imprime « Sans changement ».", { n: opo })}
          </Banner>
        )}
      </Presence>
      <div className="relative flex min-h-0 flex-1">
        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="mx-auto flex max-w-[860px] flex-col gap-10 px-8 py-10 max-sm:px-4">
            {!layoutQuery.data && <EmptyState variant="loading" compact title={t("Mise en page…")} />}
            {layoutQuery.data?.pages.map((p, i) => (
              <OrderPage
                key={i === 0 ? `reveal-${frago.id}` : p.number}
                className={i === 0 ? "opo-reveal" : undefined}
                page={p}
                layout={layoutQuery.data}
                imageUrl={imageUrl}
                activeField={activeField}
                editable={draft}
                editableFields={editable}
                onPick={(field) => {
                  setActiveField(field);
                  setEditorOpen(true);
                }}
              />
            ))}
          </div>
        </div>
        <Presence show={editorOpen && specs.length > 0}>
          {editorOpen && specs.length > 0 && (
            <SidePanel title={t("Éditer le FRAGO")} icon={FilePen} onClose={() => setEditorOpen(false)}>
              <FragoEditor
                frago={frago}
                specs={specs}
                activeField={activeField}
                readOnly={!draft || locked}
                onSave={(key, value) => edit.mutate({ key, value })}
                onReset={(key) => reset.mutate(key)}
              />
            </SidePanel>
          )}
        </Presence>
      </div>
      <Presence show={dialog === "signature"}>
        {dialog === "signature" && (
          <SignaturePad hasSignature={frago.has_signature} onClose={() => setDialog(null)} onSave={(png) => sign.mutate(png)} onDelete={() => unsign.mutate()} />
        )}
      </Presence>
      <Presence show={dialog === "delete"}>
        {dialog === "delete" && (
          <ConfirmDialog
            title={t("Supprimer le FRAGO N°{n} ?", { n: number })}
            confirmLabel={t("Supprimer")}
            busy={remove.isPending}
            busyLabel={t("Suppression…")}
            onConfirm={() => remove.mutate()}
            onCancel={() => setDialog(null)}
          >
            {t("Le FRAGO en rédaction est supprimé ; l'OPO N°{n} n'est pas modifié.", { n: opo })}
          </ConfirmDialog>
        )}
      </Presence>
    </div>
  );
}

/** Éditeur des champs du FRAGO : un champ par paragraphe, enregistré à la sortie du champ ; vide, il s'imprime
 *  « Sans changement » (sauf le § 2, toujours rappelé). Le champ actif (clic dans la page) est amené en vue. */
function FragoEditor({
  frago,
  specs,
  activeField,
  readOnly,
  onSave,
  onReset,
}: {
  frago: Frago;
  specs: FragoFieldSpec[];
  activeField: string | null;
  readOnly: boolean;
  onSave: (key: string, value: string) => void;
  onReset: (key: string) => void;
}) {
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  useEffect(() => setDrafts({}), [frago.version]);
  useEffect(() => {
    if (!activeField) return;
    document.querySelector(`[data-attention="${fieldTarget(activeField)}"]`)?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [activeField]);
  return (
    <div className="divide-y divide-line">
      {specs.map((spec) => {
        const stored = frago.fields[spec.key] ?? "";
        const value = drafts[spec.key] ?? stored;
        const edited = frago.edited.includes(spec.key);
        const changed = frago.detected.includes(spec.key);
        return (
          <section key={spec.key} className="space-y-1.5 px-4 py-3" data-attention={fieldTarget(spec.key)}>
            <div className="flex min-h-8 items-center gap-2 py-1.5">
              <span className="text-ui font-semibold text-ink">{fieldLabel(spec)}</span>
              {changed && <Badge tone="accent">{t("Changé depuis l'OPO")}</Badge>}
              {edited && <Badge tone="warn">{t("Modifié")}</Badge>}
              {edited && !readOnly && (
                <Button size="sm" variant="ghost" className="ml-auto" onClick={() => onReset(spec.key)} title={t("Reprendre le texte relevé d'après le dossier")}>
                  <RotateCcw size={13} /> {t("Rétablir")}
                </Button>
              )}
            </div>
            <textarea
              className={textareaClass}
              rows={spec.kind === "line" ? 1 : Math.min(12, Math.max(3, value.split("\n").length + 1))}
              value={value}
              readOnly={readOnly}
              placeholder={spec.key === "frago2" ? t("La mission est toujours rappelée") : spec.kind === "text" ? t("Sans changement") : undefined}
              onChange={(e) => setDrafts((d) => ({ ...d, [spec.key]: e.target.value }))}
              onBlur={() => {
                const typed = drafts[spec.key];
                if (!readOnly && typed !== undefined && typed !== stored) onSave(spec.key, typed);
              }}
            />
          </section>
        );
      })}
      <Motion origin="fade">
        <p className="px-4 py-3 text-meta leading-snug text-ink-muted">
          {t("Un paragraphe vide s'imprime « Sans changement ». Le § 2 rappelle toujours la mission.")}
        </p>
      </Motion>
    </div>
  );
}
